import { useQuery } from '@tanstack/react-query'
import { 
	getAllConversations, 
	getConversationsForOrder, 
	getConversationByThreadId,
	getUnreadConversationsCount,
	type Conversation 
} from '@/data/mockOrdersData'

export interface ConversationsFilters {
	search?: string
	channel?: string
	unreadOnly?: boolean
	orderId?: string
}

export interface ConversationsQueryParams {
	filters?: ConversationsFilters
}

export interface ConversationsQueryResult {
	conversations: Conversation[]
	total: number
	unreadCount: number
}

// Mock API for conversations
const mockConversationsApi = async (params: ConversationsQueryParams = {}): Promise<ConversationsQueryResult> => {
	// Simulate network delay
	await new Promise(resolve => setTimeout(resolve, 150 + Math.random() * 200))
	
	let conversations = getAllConversations()
	
	// Apply filters
	if (params.filters) {
		const { search, channel, unreadOnly, orderId } = params.filters
		
		if (orderId) {
			conversations = getConversationsForOrder(orderId)
		}
		
		if (search) {
			const searchLower = search.toLowerCase()
			conversations = conversations.filter(conv => {
				const lastMessage = conv.messages[conv.messages.length - 1]
				return (
					lastMessage?.message_content?.toLowerCase().includes(searchLower) ||
					lastMessage?.summary?.toLowerCase().includes(searchLower)
				)
			})
		}
		
		if (channel) {
			conversations = conversations.filter(conv => conv.channel === channel)
		}
		
		if (unreadOnly) {
			conversations = conversations.filter(conv => conv.unread_count > 0)
		}
	}
	
	return {
		conversations,
		total: conversations.length,
		unreadCount: getUnreadConversationsCount()
	}
}

export const useConversationsQuery = (params: ConversationsQueryParams = {}) => {
	return useQuery({
		queryKey: ['conversations', params],
		queryFn: () => mockConversationsApi(params),
		staleTime: 30000, // 30 seconds
		refetchOnWindowFocus: false,
	})
}

// Hook for single conversation details
export const useConversationQuery = (threadId: string) => {
	return useQuery({
		queryKey: ['conversation', threadId],
		queryFn: async () => {
			// Simulate network delay
			await new Promise(resolve => setTimeout(resolve, 100))
			const conversation = getConversationByThreadId(threadId)
			if (!conversation) {
				throw new Error('Conversation not found')
			}
			return conversation
		},
		enabled: !!threadId,
		staleTime: 30000,
		refetchOnWindowFocus: false,
	})
}

// Hook for unread count (for badges)
export const useUnreadCountQuery = () => {
	return useQuery({
		queryKey: ['unread-count'],
		queryFn: async () => {
			// Simulate network delay
			await new Promise(resolve => setTimeout(resolve, 50))
			return getUnreadConversationsCount()
		},
		staleTime: 10000, // 10 seconds - more frequent updates for unread count
		refetchInterval: 30000, // Auto-refresh every 30 seconds
		refetchOnWindowFocus: true,
	})
}
